import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "../components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_landscape-pros-12/artifacts/pgiiqdu2_Untitled%20design%20%281%29.png";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Pricing", path: "/pricing" },
  { name: "Gallery", path: "/gallery" },
  { name: "About", path: "/about" },
  { name: "Contact", path: "/contact" },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  return (
    <nav data-testid="navbar" className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="navbar-logo">
            <img 
              src={LOGO_URL} 
              alt="Elite Blade Landscaping" 
              className="h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`px-4 py-2 text-sm font-accent transition-colors ${
                  location.pathname === link.path
                    ? "text-primary"
                    : "text-gray-700 hover:text-primary"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <a href="tel:7134999372" className="flex items-center text-sm text-gray-700 hover:text-primary">
              <Phone className="h-4 w-4 mr-2 text-primary" />
              (713) 499-9372
            </a>
            <Link to="/contact">
              <Button 
                data-testid="navbar-cta"
                className="bg-accent hover:bg-accent/90 text-black font-accent active:scale-95 transition-transform"
              >
                Get a Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden p-2 text-gray-700 hover:text-primary"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden pb-4 bg-white" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                className={`block px-4 py-3 text-sm font-accent transition-colors ${
                  location.pathname === link.path
                    ? "text-primary"
                    : "text-gray-700 hover:text-primary"
                }`}
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <div className="px-4 pt-4 space-y-3">
              <a href="tel:7134999372" className="flex items-center text-sm text-gray-700">
                <Phone className="h-4 w-4 mr-2 text-primary" />
                (713) 499-9372
              </a>
              <Link to="/contact" onClick={() => setIsOpen(false)}>
                <Button className="w-full bg-accent hover:bg-accent/90 text-black font-accent">
                  Get a Quote
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-gray-900 border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Logo & About */}
          <div className="md:col-span-2">
            <img 
              src={LOGO_URL} 
              alt="Elite Blade Landscaping" 
              className="h-20 w-auto mb-4"
            />
            <p className="text-gray-400 max-w-md">
              Elevating Every Property. Professional landscaping and grounds care 
              services in the Greater Houston area. Insured, reliable, and committed 
              to excellence.
            </p>
            <div className="flex items-center mt-6 space-x-2">
              <span className="px-3 py-1 bg-primary/20 text-primary text-xs font-accent rounded">Insured</span>
              <span className="px-3 py-1 bg-primary/20 text-primary text-xs font-accent rounded">Reliable</span>
              <span className="px-3 py-1 bg-primary/20 text-primary text-xs font-accent rounded">Professional</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-heading text-lg mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-gray-400 hover:text-primary transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-heading text-lg mb-4">Contact Us</h4>
            <ul className="space-y-3 text-sm text-gray-400">
              <li>
                <span className="text-primary font-semibold">Damion</span>
              </li>
              <li>
                <a href="tel:7134999372" className="hover:text-primary transition-colors">
                  (713) 499-9372
                </a>
              </li>
              <li>
                <a href="mailto:info@elitebladelgc.com" className="hover:text-primary transition-colors">
                  info@elitebladelgc.com
                </a>
              </li>
              <li className="pt-2">
                <span className="text-gray-500">Serving Houston & Surrounding Areas</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Elite Blade Landscaping & Grounds Care. All rights reserved.
          </p>
          <p className="text-gray-600 text-xs mt-2 md:mt-0">
            Elevating Every Property
          </p>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-20">
        {children}
      </main>
      <Footer />
    </div>
  );
};
